// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

import { Modal, ModalAction } from "components/modal/modal";
import { t } from "utilities/localization/t";
import React, { JSX, useState } from "react";
import { ButtonStyle } from "components/buttons/button/button";
import { HubspotForm } from "components/form/hubspot/hubspot-form";
import { useHubspotForm } from "components/form/hubspot/use-hubspot-form";
import { ToastManager } from "utilities/toast/toast-manager";

interface SupportSubmitQuestionModalProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constant
// ------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "support-submit-question-modal";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const SupportSubmitQuestionModal: React.FC<SupportSubmitQuestionModalProps> = ({
    open,
    setOpen,
}: SupportSubmitQuestionModalProps): JSX.Element => {
    const { onSubmit, resetFormState, dirty, setDirty, ...hubspotFormState } = useHubspotForm();
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);

    const onModalClose = (): void => {
        if (dirty) {
            setShowConfirmationModal(true);
        } else {
            resetFormState();
            setOpen(false);
        }
    };

    const modalActions: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => onModalClose(),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("submit"),
            onClick: async () => {
                if (await onSubmit()) {
                    resetFormState();
                    ToastManager.success(t("successfullySubmittedQuestion"));
                    setOpen(false);
                }
            },
            disabled: false,
            style: ButtonStyle.Primary,
        },
    ];

    const confirmationActions: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => setShowConfirmationModal(false),
            style: ButtonStyle.Secondary,
        },
        {
            buttonText: t("confirm"),
            onClick: () => {
                resetFormState();
                setShowConfirmationModal(false);
                setOpen(false);
            },
            style: ButtonStyle.Destructive,
        },
    ];

    return (
        <Modal
            actions={modalActions}
            cssClassName={CSS_CLASS_NAME}
            isOpen={open}
            onModalClose={onModalClose}
            title={t("submitAQuestion")}>
            <div>
                <HubspotForm {...hubspotFormState} />
            </div>
            <Modal
                isOpen={showConfirmationModal}
                onModalClose={() => {}}
                actions={confirmationActions}
                modalStyle={"-inverted"}>
                {t("youHaveUnsavedChanges")}
                <br></br> {t("areYouSureYouWantToExit")}
            </Modal>
        </Modal>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { SupportSubmitQuestionModal };

// #endregion Exports
